import React, { useCallback, useEffect } from 'react';
import { Popover, IconButton, Tooltip, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Log } from '../../../types';
import { getDateString, getTimezoneAbbr } from 'js/lib/DateUtils';
import { logSeverityClassMap } from '../LogLine';
import { LogCopyIconButton } from '../../../components/LogCopyIconButton';
import styles from './LogDetailPopover.module.scss';

type LogDetailPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  logObject: Log;
  onClickPreviousLog?: () => void;
  onClickNextLog?: () => void;
};

export const LogDetailPopover: React.FC<LogDetailPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  logObject,
  onClickNextLog,
  onClickPreviousLog,
}) => {
  const { timestamp, resource_type, resource_id, severity, log } = logObject;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowLeft':
          if (onClickPreviousLog) {
            event.preventDefault();
            onClickPreviousLog();
          }
          break;
        case 'ArrowRight':
          if (onClickNextLog) {
            event.preventDefault();
            onClickNextLog();
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, onClickPreviousLog, onClickNextLog, onClose]);

  const formatTime = (ts: number | undefined) => {
    if (!ts) return 'N/A';
    const timezone = getTimezoneAbbr();
    const timestampString = getDateString(ts, 'YYYY-MM-DD HH:mm:ss');
    return `${timestampString} ${timezone}`;
  };

  const formatSource = useCallback(() => {
    if (resource_type) {
      return resource_type.toUpperCase();
    }
    return 'N/A';
  }, [resource_type]);

  const formatResourceId = useCallback(() => {
    if (resource_id) {
      return String(resource_id);
    }
    return 'N/A';
  }, [resource_id]);

  const isPreviousDisabled = !onClickPreviousLog;
  const isNextDisabled = !onClickNextLog;

  const logSeverityClass = severity
    ? logSeverityClassMap[severity] || styles.logSeverityInfo
    : styles.logSeverityInfo;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          className: styles.popoverPaper,
        },
      }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.navigationControls}>
            <Tooltip title="Previous log" placement="bottom" arrow>
              <IconButton
                size="small"
                className={`${styles.navigationIconButton} ${isPreviousDisabled ? styles.disabled : ''}`}
                onClick={onClickPreviousLog}
                disabled={isPreviousDisabled}
                aria-label="Previous log"
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box className={styles.navigationSeparator} />

            <Tooltip title="Next log" placement="bottom" arrow>
              <IconButton
                size="small"
                className={`${styles.navigationIconButton} ${isNextDisabled ? styles.disabled : ''}`}
                onClick={onClickNextLog}
                disabled={isNextDisabled}
                aria-label="Next log"
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          <div className={styles.headerTitle}>
            <span className={`${styles.severityBadge} ${logSeverityClass}`}>
              {severity || 'N/A'}
            </span>
          </div>

          <div className={styles.headerControls}>
            <IconButton
              onClick={onClose}
              size="small"
              className={styles.closeButton}
              aria-label="Close popover"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.fieldsGrid}>
            <div className={styles.field}>
              <div className={styles.label}>TIMESTAMP</div>
              <div className={styles.value}>{formatTime(timestamp)}</div>
            </div>

            <div className={styles.field}>
              <div className={styles.label}>RESOURCE TYPE</div>
              <div className={styles.value}>{formatSource()}</div>
            </div>

            <div className={styles.field}>
              <div className={styles.label}>RESOURCE ID</div>
              <div className={styles.value}>{formatResourceId()}</div>
            </div>
          </div>

          <div className={styles.messageSection}>
            <div className={styles.messageHeader}>
              <span className={styles.label}>MESSAGE</span>
            </div>
            <div className={styles.messageContentWrapper}>
              <p className={styles.messageContent}>{log || 'No message available'}</p>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Typography variant="caption" className={styles.keyboardHintText}>
            Use ← → arrows to navigate, ESC to close
          </Typography>
          <LogCopyIconButton
            log={logObject}
            className={styles.copyEntireLogButton}
            copiedClassName={styles.copyEntireLogButton_copied}
            label="Copy Log"
            copiedLabel="Log Copied"
            title="Copy entire log"
          />
        </div>
      </div>
    </Popover>
  );
};
